import { bindable } from 'aurelia-templating';
import { autoinject, transient } from 'aurelia-dependency-injection';
import * as Enumerable from 'linq';
import { Messages } from '../../../configuration/event-messages.constants';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { BindingEngine, computedFrom, observable } from 'aurelia-binding';
import { Guid } from '../../../utilities/guid';
import { ViewEngine, Container, View } from 'aurelia-framework';
import { Reflection } from '../../../utilities/reflection';

@autoinject
@transient()
export class MultiSelectComboBox {

    private _indexFocusedLi: number = 0;
    private _subscriptionToDocumentClickEvent: Subscription;
    private _scrollViewerTop: number;
    private _invertDropdown: boolean;
    private _isAttached: boolean;
    private _itemsToHideObserver: Subscription;

    public dropdownElement: any;
    public topDivBuffer: HTMLElement;
    public bottomDivBuffer: HTMLElement;
    public dropDownContext: DropDownContext;
    public displaySearchPlaceholder: boolean = true;
    public multiSelectComboBoxInputElement: HTMLInputElement;
    
    @bindable
    public identifier: string;
    

    @observable()
    public filteredItemSource: any[];

    @bindable
    public searchValue: string;

    @bindable
    public addSelectAll: boolean = true;

    @bindable
    public itemSource: any[];

    @bindable
    public readonly: boolean;

    @bindable
    public disabled: boolean;

    @bindable
    public selectedItems: any[] = [];

    @bindable
    public placeholder: string = '';

    @bindable
    public label: string;

    @bindable
    public info: string = '';

    @bindable
    public infoBottom: string = '';

    @bindable
    public itemsToHide: any[] = [];

    @bindable
    public setDefault: boolean | ((itemSource: any[]) => any);

    @bindable
    public isDropdownOpen: boolean;

    @bindable
    public showOnAdorner: boolean = true;

    @bindable
    public isSearchable: boolean = true;

    @bindable
    public isEditing: boolean;

    @bindable
    public displayName: string;

    @bindable
    public orderSelectedFirst: boolean = true;

    @bindable
    public displayMemberPath: string | ((item) => string);

    @bindable
    public disableInputForPlaceholder: boolean = false;
    
    dropdown: View;

    // The initial 'scrollableItemCount' value
    initialScrollableItemCount: number = 20;

    // The number of items that are rendered within the scrollableDiv
    scrollableItemCount: number;

    // Determines whether the fast-scrolling measures are taken
    fastScrollingCheckEnabled: boolean;

    // Maintains intersection observation of the scrollable items within the scrollableDiv
    itemsIntersectionObserver: IntersectionObserver = null;

    // The starting item's index to be rendered (the index pertains to all loaded dropdown items)
    startingIndex: number = 0;

    // The ending item's index to be rendered (the index pertains to all loaded dropdown items)
    endingIndex: number = 0;

    // Number of items that are added to the start/end of the rendered items array
    itemIncrementDecrementCount = 1;

    // Number of items near the 'start' or 'end' index that load more items
    itemIntersectionTreshold = 2;

    // Rendered dropdown items
    visibleScrollableItems: any[] = [];

    // Top virtual padding (above visibleScrollableItems)
    topVirtualPadding: number;

    // Bottom virtual padding (below visibleScrollableItems)
    bottomVirtualPadding: number;

    // Average dropdown item height
    averageItemHeight: number = 40;

    // Reference to the scrollable div (contains all rendered dropdown items)
    scrollableDiv: Element;

    // The 'scrollY' value of the scrollableDiv
    scrollableDivScrollY: number = 0;

    // Stores the reference to the 'setInterval' tracking the fast scrolling
    scrollIntervalId;

    // The interval (ms) by which the interval timeout is checked (referred to as scroll interval)
    scrollIntervalTimeout = 200;

    // The amount of scroll units that (if performed within the 'scrollIntervalTimeout') indicate fast scrolling being performed
    fastScrollAmountDelimeter = 450;

    // The amount of scroll perfomed during the last scroll interval
    previousScrollableDivScrollY: number = 0;

    // Keeps track of the scrolling state of the user (if the user is scrolling fast or not)
    isFastScrolling: boolean;

    // Determines whether the adequate rendered item count has been set
    initialisedRenderedItemsCount: boolean;

    // The number of visible items within the dropdown at a single point in time
    visibleItemsCount: number;

    // 'itemsToHide' value changed handler
    itemsToHideChanged(newValue) {
        if (this._itemsToHideObserver) {
            this._itemsToHideObserver.dispose();
            this._itemsToHideObserver = null;
        }

        if (newValue) {
            this.bindingEngine.collectionObserver(newValue).subscribe(() => {
                this.filteredItemSource = this.internalItemSource.toArray();
            });
        }
        this.filteredItemSource = this.internalItemSource.toArray();
    }

    @computedFrom(Reflection.getPropertyPath<MultiSelectComboBox>(z => z.dropdownElement), Reflection.getPropertyPath<MultiSelectComboBox>(z => z._scrollViewerTop),
        Reflection.getPropertyPath<MultiSelectComboBox>(z => z.dropdownElement.clientHeight))
    public get invertDropdown(): boolean {
        if (this.dropdownElement) {
            const scrollViewer = this.getScrollParent(this.dropdownElement);
            if (scrollViewer) {
                if (!this._scrollViewerTop) {
                    this._scrollViewerTop = this.getTop(scrollViewer);
                }

                const dropdownPosition = ((this.getTop($(this.dropdownElement).parent().get(0)) + $(this.dropdownElement).parent().get(0).clientHeight) - this._scrollViewerTop) + this.dropdownElement.clientHeight;

                if (scrollViewer.nodeType != document.DOCUMENT_FRAGMENT_NODE && scrollViewer.clientHeight) {
                    this._invertDropdown = scrollViewer.clientHeight < dropdownPosition;
                }
            }
        }
        return this._invertDropdown;
    }

    @computedFrom(Reflection.getPropertyPath<MultiSelectComboBox>(x => x.displayName), Reflection.getPropertyPath<MultiSelectComboBox>(x => x.displaySearchPlaceholder))
    public get searchPlaceholder() {
        if (this.displayName && this.displaySearchPlaceholder) {
            return this.displayName;
        }
        else {
            return "";
        }
    }

    get internalItemSource() {
        if (this.itemsToHide && this.itemsToHide.length > 0 && this.itemSource) {
            const valuesToHide = this.itemsToHide;
            const selectedItemsStringified = Enumerable.from(this.selectedItems).select(x => JSON.stringify(x));
            const condition = x => selectedItemsStringified.any(s => s == JSON.stringify(x)) || Enumerable.from(this.selectedItems).any(s => s == x);
            return Enumerable.from<any>(this.itemSource).where(x => valuesToHide.every(z => z != (x)) || condition(x));
        }
        return Enumerable.from<any>(this.itemSource);
    }

    private orderItemSource() {
        if (this.orderSelectedFirst) {
            const ordered = this.filteredItemSource ? Enumerable.from(this.filteredItemSource).orderByDescending(x => x.isSelected).toArray() : [];
            this.filteredItemSource = ordered;
        }
    }

    @computedFrom(Reflection.getPropertyPath<MultiSelectComboBox>(z => z.selectedItems), Reflection.getPropertyPath<MultiSelectComboBox>(z => z.selectedItems.length), Reflection.getPropertyPath<MultiSelectComboBox>(z => z.searchValue))
    public get selectAll(): boolean {
        return this.filteredItemSource.every(x => x.isSelected);
    }

    constructor(private element: Element, private viewEngine: ViewEngine, private eventAggregator: EventAggregator,
        private bindingEngine: BindingEngine) {
    }

    public attached(): void {
        if (this.itemSource) {
            this.itemSource.forEach(x => {x.isSelected = false});
        }
        if (this._subscriptionToDocumentClickEvent) {
            this._subscriptionToDocumentClickEvent.dispose();
        }
        this._subscriptionToDocumentClickEvent = this.eventAggregator.subscribe(Messages.documentClick, () => {
            this.closePopup();
            this.onBlurred();
        });

        if (this._isAttached) {
            return;
        }

        this._isAttached = true;
        this.dropDownContext = new DropDownContext(this);
        this.itemSourceChanged(this.itemSource, undefined);

        this.viewEngine.loadViewFactory('views/components/multi-select-combo-box/multi-select-dropdown.html').then(response => {
            this.dropdown = response.create(Container.instance);
        });

        window.addEventListener('resize', this.resetDropdownBounds);
    }

    resetDropdownBounds = () => {
        this.initialisedRenderedItemsCount = false;
    }

    public detached(): void {
        this._isAttached = false;
        if (this._subscriptionToDocumentClickEvent) {
            this._subscriptionToDocumentClickEvent.dispose();
        }
        if (this.dropDownContext) {
            this.dropDownContext.comboBoxContext = null;
            this.dropDownContext = null;
        }
        if (this._itemsToHideObserver) {
            this._itemsToHideObserver.dispose();
        }

        this.clearProperties();

        window.removeEventListener('resize', this.resetDropdownBounds);
    }

    /**
     * Reset properties
     */
    public clearProperties() {
        this.fastScrollingCheckEnabled = false;
        this.scrollableDivScrollY = 0;
        this.previousScrollableDivScrollY = 0;
        this.scrollableItemCount = this.initialScrollableItemCount;
        this.topVirtualPadding = 0;
        this.bottomVirtualPadding = 0;

        this.visibleScrollableItems = [];

        // If the top & bottom div buffers are visible
        if (this.topDivBuffer && this.bottomDivBuffer) {
            // Reset scroll and padding
            this.scrollableDiv.scrollTop = 0;
            this.assignMultiSelectPadding();
        }

        this.disconnectObjectIntersectors();

        window.clearInterval(this.scrollIntervalId);
    }

    /**
     * Initialise the dropdown properties (including virtual-repeat)
     */
    public initialiseDropdownProperties() {
        this.topDivBuffer = document.getElementById('multi-select-combo-box--dropdown-top-buffer');
        this.bottomDivBuffer = document.getElementById('multi-select-combo-box--dropdown-bottom-buffer');
        this.assignMultiSelectPadding();

        this.previousScrollableDivScrollY = 0;

        this.assignInitialScrollableItems();

        // Wait for dropdown items to appear
        setTimeout(() => {
            this.connectIntersectionObservers(!this.initialisedRenderedItemsCount);

            // Wait for 'visibleItemsCount' to be computed
            setTimeout(() => {
                // Check only for fast scrolling if there is a minimum dropdown item count
                if (this.filteredItemSource.length > (this.visibleItemsCount * 4)) {
                    this.fastScrollingCheckEnabled = true;

                    // Setup scroll frequency interval check (checks if user is scrolling fast or not)
                    this.scrollIntervalId = setInterval(() => {
                        this.checkScrollFrequency();
                    }, this.scrollIntervalTimeout);
                }
            }, 200);

            // Store reference to scrollable div
            this.scrollableDiv = document.getElementById('multi-select-combo-box--dropdown-scroller');

            this.scrollableDiv.addEventListener('scroll', this.assignScrollableDivScrollY);
        }, 200)
    }

    assignScrollableDivScrollY = () => {
        this.scrollableDivScrollY = this.scrollableDiv.scrollTop;
    }

    /**
     * 'filteredItemSource' value changed event
     */
    filteredItemSourceChanged() {
        this.clearProperties();

        if (this.dropDownContext?.isDropdownOpen) {
            // Wait for initial elements (such as top & bottom div-buffers to appear)
            setTimeout(()=>{
                this.initialiseDropdownProperties();
            }, 10);
        }
    }

    /**
     * Assign the initialScrollableItems, including the starting and ending indexes
     */
    private assignInitialScrollableItems() {
        // Make sure initial item count does not exceed existing item count
        this.scrollableItemCount = Math.min(this.initialScrollableItemCount, this.filteredItemSource.length);

        this.startingIndex = 0;
        // 1 is reduced from the endingIndex because the startingIndex is inclusive
        this.endingIndex = this.scrollableItemCount - 1;
        this.setVisibleScrollableItems(this.startingIndex, this.startingIndex + this.scrollableItemCount);
    }

    /**
     * Assign padding to the multi-select scrollable div
     */
    public assignMultiSelectPadding() {
        this.topVirtualPadding = this.averageItemHeight * this.startingIndex;
        this.bottomVirtualPadding = this.averageItemHeight * (this.filteredItemSource.length - (this.endingIndex + 1));

        this.topDivBuffer?.style.setProperty('height', `${this.topVirtualPadding}px`);
        this.bottomDivBuffer?.style.setProperty('height', `${this.bottomVirtualPadding}px`);
    }

    /**
     * Check if the user performed a fast scroll within the last scroll interval
     */
    checkScrollFrequency() {
        // Compute the vertical Y scroll carried out by the user in the last scroll interval
        const amountScrolled = Math.abs(this.scrollableDivScrollY - this.previousScrollableDivScrollY);
        this.previousScrollableDivScrollY = this.scrollableDivScrollY;

        // If the current scroll frequency is fast
        if (amountScrolled >= this.fastScrollAmountDelimeter) {
            this.disconnectObjectIntersectors();
            this.isFastScrolling = true;
            this.computeFastScrollingItems();
        }
        // If the current scroll frequency was fast, but is not anymore
        else if (this.isFastScrolling) {
            this.isFastScrolling = false;
            this.connectIntersectionObservers();
        }
    }

    /**
     * Display the corresponding dropdown items that represent the amount of scroll performed by the user
     */
    public computeFastScrollingItems() {
        // Stop the scroll interval (this will be restarted later in this method)
        window.clearInterval(this.scrollIntervalId);

        // Determines whether the scrollbar should be scrolled to the bottom (Is 'true' if the last dropdown item is to be visible)
        let scrollToBottom: boolean;

        // The topItemsOffset decreases the starting index to include more items on the top side of the scrollable div (as a buffer)
        const topItemsOffset = this.itemIntersectionTreshold + 2;
        this.startingIndex = Math.max(0, Math.floor(this.scrollableDivScrollY / this.averageItemHeight) - topItemsOffset) ;

        // 1 is subtracted from the 'endingIndex' because the 'startingIndex' is inclusive within the scrollable items count
        this.endingIndex = Math.min(this.startingIndex + (this.scrollableItemCount - 1), this.filteredItemSource.length - 1);

        // If the last dropdown item is visible,
        if (this.endingIndex === (this.filteredItemSource.length - 1)) {
            // Compute the startingIndex. 1 is subtracted from the 'startingIndex' because the 'endingIndex' is inclusive
            this.startingIndex = Math.max(0, this.endingIndex - (this.scrollableItemCount - 1));

            scrollToBottom = true;
        }

        // Update rendered dropdown items
        this.setVisibleScrollableItems(this.startingIndex, this.startingIndex + this.scrollableItemCount);

        // Reconnect intersection observers
        this.connectIntersectionObservers();

        if (scrollToBottom) {
            this.scrollableDiv.scrollTop = this.scrollableDiv.scrollHeight;
        }

        if (this.fastScrollingCheckEnabled) {
            this.scrollIntervalId = setInterval(() => {
                this.checkScrollFrequency();
            }, this.scrollIntervalTimeout);
        }
    }

    /**
     * Get scrollable elements rendered within the dropdown
     */
    getScrollableElements = () => document.querySelectorAll('.multi-select-combo-box--dropdown-item.scrollable-element');

    /**
     * Assign an index to each rendered scrollable element based on the starting index
     */
    public assignDataIndex() {
        const scrollableElements = this.getScrollableElements();

        let counter = 0;
        scrollableElements.forEach((scrollableElement: Element) => {
            scrollableElement.removeAttribute('data-index');
            scrollableElement.setAttribute('data-index', String(counter + this.startingIndex));
            counter++;
        })
    }

    /**
     * Set the number of items to be rendered within the dropdown depending on its height and average item height
     */
    initialiseDropdownBounds(visibleItemCount: number, averageItemHeight: number) {
        // Add a 'buffer' item count to the initialVisibleItemCount
        this.initialScrollableItemCount = visibleItemCount + (this.itemIntersectionTreshold * 2) + 5;
        this.visibleItemsCount = visibleItemCount;
        this.averageItemHeight = Math.ceil(averageItemHeight); // Avoid floating points, to decrease memory and computational burden

        // Disconnect existing objectIntersectors
        this.disconnectObjectIntersectors();
        // Re-assign scrollableItemCount, and start & ending indexes
        this.assignInitialScrollableItems();
        // Reconnect objectIntersectors
        this.connectIntersectionObservers();
    }

    /**
     * Connect intersection observers to each rendered dropdown item
     * @param initialiseItemsCount If 'true', this method will compute how many items should be rendered within the dropdown
     */
    public connectIntersectionObservers(initialiseItemsCount?: boolean) {
        // Assign top and bottom virtual padding
        this.assignMultiSelectPadding();

        // Assign indexes to dropdown items
        this.assignDataIndex();

        const intersectionObserverOptions = {
            root: this.scrollableDiv,
            rootMargin: '0px',
            treshold: 1
        }

        // Initialise intersection observer
        this.itemsIntersectionObserver = new IntersectionObserver((entries: IntersectionObserverEntry[]) => {
            // If set to 'true' later, the visible dropdown items are updates
            let changeRequested: boolean;

            // If 'true', the rendered dropdown items count will be computed
            if (initialiseItemsCount) {
                const visibleItems: IntersectionObserverEntry[] = entries.filter((entry: IntersectionObserverEntry) => entry.isIntersecting);

                // Only proceed if there are visible items
                if (visibleItems.length > 0) {
                    const visibleItemsAverageHeight: number = visibleItems.map((entry: IntersectionObserverEntry) => entry.boundingClientRect.height)
                    .reduce((a: number, b: number) => a + b) / visibleItems.length;

                this.initialisedRenderedItemsCount = true;
                this.initialiseDropdownBounds(visibleItems.length, visibleItemsAverageHeight);
                } else {
                // Handle case where no items are visible
                    this.initialisedRenderedItemsCount = false;
                    this.initialiseDropdownBounds(0, 0); 
                } 
            }

            // Loop over entries which reported a change in their intersection status
            entries.forEach((entry: IntersectionObserverEntry) => {
                const entryIndex: number = parseInt(entry.target.getAttribute('data-index'));

                // If the item two placed prior the last item is visible, and the last filtered item is not visible yet
                if ((entryIndex+this.itemIntersectionTreshold) >= this.endingIndex && entry.isIntersecting && this.endingIndex < (this.filteredItemSource.length-1)) {
                    this.incrementScrollableIndexes(this.itemIncrementDecrementCount);
                    changeRequested = true;
                }

                // If the item two placed after the first item is visible, and the first filtered item is not visible yet
                if ((entryIndex-this.itemIntersectionTreshold) <= this.startingIndex && entry.isIntersecting && this.startingIndex > 0) {
                    this.decrementScrollableIndexes(this.itemIncrementDecrementCount);
                    changeRequested = true;
                }
            });

            if (changeRequested) {
                this.setVisibleScrollableItems(this.startingIndex, this.startingIndex + this.initialScrollableItemCount);

                setTimeout(() => {
                    this.disconnectObjectIntersectors();
                    setTimeout(() => {
                        this.connectIntersectionObservers();
                    })
                }, 50)
            }
        }, intersectionObserverOptions);

        const scrollableElements = this.getScrollableElements();

        Array.from(scrollableElements).forEach((scrollableElement: Element) => {
            this.itemsIntersectionObserver.observe(scrollableElement)
        });
    }

    /**
     * Set visible scrollable items
     * @param startIndex
     * @param endingIndex
     */
    public setVisibleScrollableItems(startIndex: number, endingIndex: number) {
        this.visibleScrollableItems = this.filteredItemSource.slice(startIndex, endingIndex);
    }

    /**
     * Increment indexes of scrollable elements
     * @param count Amount to increment by
     */
    public incrementScrollableIndexes(count: number) {
        const incrementCount = Math.min(count, this.filteredItemSource.length - (this.endingIndex + 1));

        this.startingIndex += incrementCount;
        this.endingIndex += incrementCount;
    }

    /**
     * Decrement indexes of scrollable elements
     * @param count Amount to decrement by
     */
    public decrementScrollableIndexes(count: number) {
        const decrementCount = Math.min(count, this.startingIndex);

        this.startingIndex -= decrementCount;
        this.endingIndex -= decrementCount;
    }

    /**
     * Disconnect any existing object intersectors
     */
    public disconnectObjectIntersectors() {
        this.itemsIntersectionObserver?.disconnect();
    }

    public showSearchPlaceholder() {
        this.displaySearchPlaceholder = true;
        this.searchValue = null;
    }

    public hideSearchPlaceholder() {
        this.displaySearchPlaceholder = false;
    }

    public getScrollParent(node) {
        if (node === null) {
            return null;
        }

        if (this.hasScroll(node)) {
            return node;
        } else {
            return this.getScrollParent(node.parentNode);
        }
    }

    public hasScroll(el: any): boolean {
        if (el.nodeType != document.ELEMENT_NODE) {
            return true;
        }

        const $el = $(el),
            sY = $el.css('overflow-y'),
            hidden = 'hidden', // minifiers would like this better
            visible = 'visible',
            scroll = 'scroll';

        if (sY === hidden || sY === visible) { return false; }
        if (sY === scroll) { return true; }

        // compare client and scroll dimensions to see if a scrollbar is needed

        return $el.innerHeight() < el.scrollHeight || // make use of potential short circuit
            $el.innerWidth() < el.scrollWidth;
    }

    public selectItemByIndex(index: number): void {
        this.select(this.filteredItemSource[index]);
    }

    public onKeyDownNavigation($event: KeyboardEvent): void {
        const code = $event.keyCode;
        if (code === 13) {
            this.selectItemByIndex(this._indexFocusedLi);
        } else if (code === 38) {
            this._indexFocusedLi = Math.max(0, this._indexFocusedLi - 1);
        } else if (code === 40) {
            this._indexFocusedLi = Math.min(this.filteredItemSource.length - 1, this._indexFocusedLi + 1);
        }
    }

    /**
     * Set the 'text' value displayed inside the component when its not expanded
     */
    public setDisplayName() {
        const joinedString = Enumerable.from(this.itemSource).where(x => x.isSelected).select(x => this.getDisplayName(x)).toArray().join(', ');

        const allSelectedTrue = this.itemSource && this.itemSource.length > 1
            && Enumerable.from(this.itemSource).all(x => x.isSelected);

        if (allSelectedTrue && this.addSelectAll) {
            this.displayName = "All";
            return;
        }
        this.displayName = joinedString;
    }

    /**
     * 'selectedItems' value changed handler
     * @param newValues
     * @param oldValues
     */
    public selectedItemsChanged(newValues: any[], oldValues: any[]): void {
        if (newValues === oldValues || !this._isAttached) {
            return;
        }
        this._indexFocusedLi = this.filteredItemSource.indexOf(this.selectedItems) % this.filteredItemSource.length;
        Enumerable.from(this.itemSource).forEach(item => {
            item.isSelected = Enumerable.from(newValues).any(x => x.id == item.id);
        });
        const itemChangedEvent = new CustomEvent('selected-items-changed', { detail: { item: newValues, oldItem: oldValues }, bubbles: true });
        this.element.dispatchEvent(itemChangedEvent);
        this.setDisplayName();
    }

    /**
     * 'itemSource' value changed handler
     * @param newValues
     * @param oldValue
     */
    public itemSourceChanged(newValues: any[], oldValue: any[]): void {
        if (JSON.stringify(newValues) === JSON.stringify(oldValue) || !this._isAttached) {
            return;
        }

        this.searchValue = '';
        if (newValues) {
            newValues.forEach(x => {
                x.isSelected = x.isSelected || this.selectedItems.some(y => y == x || x.id == y.id);
                x.identifier = Guid.generateGuid(); //needed for checkbox
            });
            this.onSelectedChanged(null);
        }

        this.filteredItemSource = this.internalItemSource.toArray();
        this.setDisplayName();
    }

    /**
     * 'searchValue' value changed handler
     * @param newValue
     * @param oldValue
     */
    public searchValueChanged(newValue: string): void {
        this.applySearchFilter(newValue);
    }

    applySearchFilter(newValue: string) {
        if (this.internalItemSource) {
            if (newValue != null && newValue.length > 0) {
                this.filteredItemSource = this.internalItemSource
                    .where(
                        x => {
                            const displayName = x.name;
                            return displayName && displayName.toString().toLowerCase().indexOf(newValue.toString().toLowerCase()) !== -1
                        }
                    ).toArray();
            } else {
                this.filteredItemSource = this.internalItemSource.toArray();
            }
            this.orderItemSource();
        }
    }

    public select = (item: any): void => {
        if (item != null) {
            this.onSelectedChanged(null);
        }
    };

    public onSelect(): void {
        setTimeout(() => {
            const paramsEvent: any = { detail: this.selectedItems, bubbles: true };
            const changeEvent: CustomEvent = new CustomEvent('items-selected', paramsEvent);
            this.element.dispatchEvent(changeEvent);
        });
    }

    public open(event: Event): void {
        if (!this.disabled && !this.readonly && !this.isDropdownOpen) {
            if (!this.isDropdownOpen) {
                event.stopPropagation();
                this.eventAggregator.publish(Messages.documentClick, {});
                this.isEditing = !this.isEditing;
                this.isDropdownOpen = !this.isDropdownOpen;
                setTimeout(() => {
                    if (this.multiSelectComboBoxInputElement)
                        this.multiSelectComboBoxInputElement.focus();
                });
            } else {
                this.isEditing = !this.isEditing;
                this.isDropdownOpen = !this.isDropdownOpen;
            }
            const internalItemSource = this.internalItemSource.toArray();
            this._indexFocusedLi = internalItemSource.indexOf(this.filteredItemSource[0]) % internalItemSource.length;
            this.orderItemSource();
        }
    }

    onFocus() {
        this.isEditing = true;
    }

    onBlurred() {
        this.isEditing = false;
    }

    public setDefaultChanged(): void {
        const source = this.internalItemSource;
        if (this.setDefault && source && (this.selectedItems == null)) {
            if (typeof this.setDefault == 'function') {
                const item = this.setDefault(source.toArray());
                if (item) {
                    this.select(item);
                }
            } else {
                this.select(source.firstOrDefault());
            }
        }
    }

    stopPropagation = (e: Event): void => {
        e.stopPropagation();
    };

    private get multiSelectName() : string {
        return this.identifier ?? this.label ?? "no_multiselect_label"
    }

    private get itemsSelected() : string {
        const stringifiedItems =  this.selectedItems.map(x => x.name).join(' | ')
        return stringifiedItems
    }

    closePopup = ($event = null) => {
        if (this.isDropdownOpen) {
            
            if(this.selectedItems.length > 0) {
                this.eventAggregator.publish(Messages.multiComboSelected, {"multiSelect" : this.multiSelectName, "itemsSelected" : this.itemsSelected})
            }

            if ($event && $event.stopPropagation) {
                $event.stopPropagation();
            }
            this.isEditing = false;
            this.isDropdownOpen = false;
            this.showSearchPlaceholder();
        }
    };

    private getTop(elem: HTMLElement): number {
        if (!elem || !elem.getBoundingClientRect) {
            return 0;
        }

        const rect = elem.getBoundingClientRect();
        const doc = elem.ownerDocument;
        const docElem = doc.documentElement;
        const win = doc.defaultView;
        return rect.top + win.pageYOffset - docElem.clientTop;
    }

    //multi select - selected items changed
    onSelectedChanged($event: any) {
        if ($event) {
            $event.stopPropagation();
        }
        setTimeout(() => {
            this.selectedItems = Enumerable.from(this.itemSource).where(x => x.isSelected).toArray();
            const paramsEvent: any = { detail: this.filteredItemSource, bubbles: true };
            const changeEvent: CustomEvent = new CustomEvent('items-selected-changed', paramsEvent);
            this.element.dispatchEvent(changeEvent);
        });
    }

    selectAllItems(event) {
        if (event) {
            event.stopPropagation();
        }
        const selectAll = this.filteredItemSource.every(x => x.isSelected);
        this.filteredItemSource.forEach(x => x.isSelected = !selectAll);
        this.onSelectedChanged(event);
        this.setDisplayName();
    }

    public getDisplayName(item: any): string {
        if (this.displayMemberPath && item) {
            if (typeof this.displayMemberPath == "function") {
                return this.displayMemberPath(item);
            } else {
                if (this.displayMemberPath.indexOf(".") > -1) {
                    const splitted = this.displayMemberPath.toString().split(".");
                    let currentMember: any = item;
                    for (const memberName of splitted) {
                        currentMember = currentMember[memberName]
                    }
                    return currentMember;
                }
                else {
                    return item[this.displayMemberPath];
                }
            }
        }
        return item.name;
    }
}

export class DropDownContext {

    constructor(public comboBoxContext: MultiSelectComboBox) { }

    @computedFrom(Reflection.getPropertyPath<DropDownContext>(z => z.comboBoxContext.isDropdownOpen))
    get isDropdownOpen(): boolean {
        return this.comboBoxContext ? this.comboBoxContext.isDropdownOpen : false;
    }
}
